import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { logError } from "@/lib/logger";
import { MapPin, Calendar, Plane } from "lucide-react";
import { EditableText as T } from "@/components/live/EditableText";
import { Section } from "@/components/live/Section";

const TourSchedule = () => {
  const [tourDates, setTourDates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Placeholder tour dates
  const placeholderDates = [
    { id: "1", city: "New York City", state: "NY", country: "USA", start_date: "2025-01-15", end_date: "2025-01-31", is_available: true, notes: "Home base - always available with notice" },
    { id: "2", city: "Los Angeles", state: "CA", country: "USA", start_date: "2025-02-10", end_date: "2025-02-17", is_available: true, notes: "Extended availability" },
    { id: "3", city: "Miami", state: "FL", country: "USA", start_date: "2025-02-28", end_date: "2025-03-05", is_available: true, notes: "Art Basel season visit" },
    { id: "4", city: "Las Vegas", state: "NV", country: "USA", start_date: "2025-03-15", end_date: "2025-03-20", is_available: true, notes: "Special event weekend" },
  ];

  const fetchTourDates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/tour-dates`);
      if (response.data && response.data.length > 0) {
        setTourDates(response.data);
      } else {
        setTourDates(placeholderDates);
      }
    } catch (error) {
      logError("Failed to fetch tour dates, using placeholders:", error);
      setTourDates(placeholderDates);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTourDates();
  }, [fetchTourDates]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div data-testid="tour-schedule-page">
      {/* Header */}
      <section className="page-header" data-testid="tour-header">
        <div className="max-w-4xl mx-auto px-6">
          <T k="tour.where-to-find" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4 animate-fade-in">Where to Find Me</T>
          <T k="tour.tour-schedule" as="h1" className="page-title animate-fade-in stagger-1">Tour Schedule</T>
          <T k="tour.check-my-upcoming" as="p" className="page-subtitle animate-fade-in stagger-2">Check my upcoming travel schedule and find the perfect time for us to connect.</T>
        </div>
      </section>

      {/* Home Bases */}
      <Section k="tour.section.home-bases" label="home bases" className="py-20 px-6 md:px-12 lg:px-24" data-testid="home-bases">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <T k="tour.primary-locations" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4">Primary Locations</T>
            <T k="tour.home-bases" as="h2" className="font-heading text-3xl text-charcoal">Home Bases</T>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="glass rounded-xl p-8 card-hover" data-testid="nyc-base">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-jade-deep/10 rounded-full flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-jade-text" />
                </div>
                <div>
                  <T k="tour.new-york-new" as="h3" className="font-heading text-xl text-charcoal">New York / New Jersey</T>
                  <T k="tour.primary-home-base" as="p" className="font-body text-sm text-taupe">Primary Home Base</T>
                </div>
              </div>
              <T k="tour.my-main-residence" as="p" className="font-body text-stone-grey leading-relaxed">My main residence in the NYC metropolitan area. Generally available with 24-48 hours notice for local engagements.</T>
            </div>

            <div className="glass rounded-xl p-8 card-hover" data-testid="la-base">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-jade-deep/10 rounded-full flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-jade-text" />
                </div>
                <div>
                  <T k="tour.california" as="h3" className="font-heading text-xl text-charcoal">California</T>
                  <T k="tour.secondary-base" as="p" className="font-body text-sm text-taupe">Secondary Base</T>
                </div>
              </div>
              <T k="tour.i-frequently-visit" as="p" className="font-body text-stone-grey leading-relaxed">I frequently visit the West Coast. Check the schedule below for my next SF/LA dates, or inquire about arranging a visit.</T>
            </div>
          </div>
        </div>
      </Section>

      {/* Tour Dates */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-blush-pink/20" data-testid="tour-dates-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <T k="tour.coming-soon" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4">Coming Soon</T>
            <T k="tour.upcoming-travel" as="h2" className="font-heading text-3xl text-charcoal">Upcoming Travel</T>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <div className="space-y-4">
              {tourDates.map((tour, index) => (
                <div 
                  key={tour.id} 
                  className="tour-card flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                  data-testid={`tour-date-${index}`}
                >
                  <div className="flex items-center gap-6">
                    <div className="hidden md:flex flex-col items-center justify-center w-20 h-20 bg-jade-deep text-white rounded-lg">
                      <span className="font-heading text-2xl">{formatDate(tour.start_date).split(' ')[1]}</span>
                      <span className="font-body text-xs uppercase">{formatDate(tour.start_date).split(' ')[0]}</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-xl text-charcoal">
                        {tour.city}, {tour.state || tour.country}
                      </h3>
                      <p className="font-body text-sm text-taupe">
                        <Calendar className="w-4 h-4 inline mr-2" />
                        {formatDate(tour.start_date)} - {formatDate(tour.end_date)}
                      </p>
                    </div>
                  </div>
                  {tour.notes && (
                    <p className="font-body text-sm text-stone-grey md:text-right">
                      {tour.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Travel Request */}
      <Section k="tour.section.travel-request" label="travel request" className="py-20 px-6 md:px-12 lg:px-24" data-testid="travel-request">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-champagne-gold/20 rounded-full flex items-center justify-center mx-auto mb-8">
            <Plane className="w-8 h-8 text-champagne-gold" />
          </div>
          <T k="tour.dont-see-your" as="h2" className="font-heading text-3xl text-charcoal mb-6">Don't See Your City?</T>
          <T k="tour.im-open-to" as="p" multiline className="font-body text-stone-grey leading-relaxed mb-8 max-w-2xl mx-auto">I'm open to travel arrangements for the right connection. If you'd like to bring me to your location or join me on an adventure elsewhere, please include details in your booking inquiry.</T>
          <T k="tour.view-travel-information" as="a" href="/travel" className="btn-secondary inline-flex items-center gap-2">View Travel Information</T>
        </div>
      </Section>

      {/* Newsletter CTA */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-jade-deep">
        <div className="max-w-4xl mx-auto text-center">
          <T k="tour.stay-updated-on" as="h2" className="font-heading text-3xl text-ivory mb-6">Stay Updated on My Travels</T>
          <T k="tour.subscribe-to-my" as="p" className="font-body text-ivory/80 mb-8">Subscribe to my newsletter to be the first to know when I'm visiting your city.</T>
          <T k="tour.join-my-newsletter" as="a" href="/#newsletter-section" className="bg-champagne-gold text-charcoal px-8 py-3 rounded-full font-body text-sm tracking-widest uppercase hover:bg-champagne transition-all duration-300 inline-block">Join My Newsletter</T>
        </div>
      </section>
    </div>
  );
};

export default TourSchedule;
